import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { questions, TOTAL_QUESTIONS } from '../data/questions';
import type { QuestionnaireData } from '../types';
import { DEFAULT_FORM_DATA } from '../types';
import { Card } from '../components/ui/Card';
import { TextArea } from '../components/ui/TextArea';
import { ProgressBar } from '../components/ui/ProgressBar';
import { QuestionCard } from '../components/QuestionCard';
import { QuestionNavigation } from '../components/QuestionNavigation';
import { useFormPersistence } from '../hooks/useFormPersistence';
import { ObjectionStepper } from '../components/ObjectionStepper';

export function Question() {
  const { step } = useParams<{ step: string }>();
  const navigate = useNavigateWithParams();
  const currentStep = parseInt(step || '1', 10);

  // Validate step number
  useEffect(() => {
    if (isNaN(currentStep) || currentStep < 1 || currentStep > TOTAL_QUESTIONS) {
      navigate('/question/1', { replace: true });
    }
  }, [currentStep, navigate]);

  const form = useForm<QuestionnaireData>({
    defaultValues: DEFAULT_FORM_DATA,
    mode: 'onChange',
  });

  const { saveStep } = useFormPersistence({ form });

  // Save step on change
  useEffect(() => {
    saveStep(currentStep);
  }, [currentStep, saveStep]);

  const [objectionComplete, setObjectionComplete] = useState(false);

  // Reset objection completion when navigating away from Q3
  useEffect(() => {
    if (currentStep !== 3) {
      setObjectionComplete(false);
    }
  }, [currentStep]);

  const question = questions[currentStep - 1];

  const handleNext = () => {
    if (currentStep < TOTAL_QUESTIONS) {
      navigate(`/question/${currentStep + 1}`);
    } else {
      navigate('/review');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigate(`/question/${currentStep - 1}`);
    } else {
      navigate('/');
    }
  };

  // Keyboard shortcut for Cmd/Ctrl + Enter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (currentStep === 3 && !objectionComplete) return;
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, objectionComplete]);

  if (!question) return null;

  return (
    <FormProvider {...form}>
      <div className="min-h-screen flex flex-col p-4 md:p-8">
        <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <ProgressBar currentStep={currentStep} />
          </motion.div>

          <Card className="flex-1 flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="flex-1"
              >
                <QuestionCard question={question}>
                  <QuestionFields
                    questionId={currentStep}
                    onObjectionComplete={() => {
                      setObjectionComplete(true);
                      handleNext();
                    }}
                  />
                </QuestionCard>
              </motion.div>
            </AnimatePresence>

            <QuestionNavigation
              currentStep={currentStep}
              totalSteps={TOTAL_QUESTIONS}
              onBack={handleBack}
              onNext={handleNext}
              isNextDisabled={currentStep === 3 && !objectionComplete}
              isLastStep={currentStep === TOTAL_QUESTIONS}
            />
          </Card>
        </div>
      </div>
    </FormProvider>
  );
}

function QuestionFields({
  questionId,
  onObjectionComplete,
}: {
  questionId: number;
  onObjectionComplete?: () => void;
}) {
  const { register, watch } = useFormContext<QuestionnaireData>();

  switch (questionId) {
    case 1:
      return (
        <>
          <TextArea
            {...register('question1.mistake')}
            id="mistake"
            label="The #1 Mistake"
            placeholder="Describe the most common financial mistake you see clients making..."
            value={watch('question1.mistake')}
          />
          <TextArea
            {...register('question1.fix')}
            id="fix"
            label="The Simple Fix"
            placeholder="What is the easiest way to fix this mistake?"
            value={watch('question1.fix')}
          />
        </>
      );

    case 2:
      return (
        <TextArea
          {...register('question2.vision')}
          id="vision"
          label="Their Thank You"
          placeholder="Be specific about the concrete outcome they are thanking you for..."
          value={watch('question2.vision')}
        />
      );

    case 3:
      return (
        <ObjectionStepper
          onAllComplete={() => onObjectionComplete?.()}
        />
      );

    case 4: {
      const q1Mistake = watch('question1.mistake');
      const q1Fix = watch('question1.fix');
      const hasQ1Data = q1Mistake?.trim() && q1Fix?.trim();

      return (
        <>
          {hasQ1Data ? (
            <div className="bg-primary-500/5 border-l-2 border-primary-500/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-400 mb-2">
                In Question 1, you said the #1 mistake is:
              </p>
              <p className="text-slate-200 text-sm font-medium mb-3 line-clamp-3">
                "{q1Mistake}"
              </p>
              <p className="text-sm text-slate-400 mb-2">And the fix is:</p>
              <p className="text-slate-200 text-sm font-medium mb-3 line-clamp-3">
                "{q1Fix}"
              </p>
              <p className="text-sm text-primary-400 font-medium">
                Now tell us — why do YOU recommend that approach?
              </p>
            </div>
          ) : (
            <div className="bg-slate-800/50 border-l-2 border-slate-600/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-400">
                💡 This question connects to Question 1. Consider completing Question 1 first for the best results.
              </p>
            </div>
          )}
          <TextArea
            {...register('question4.solution')}
            id="solution"
            label="Why Your Solution"
            placeholder="Explain why you recommend your approach over the standard advice..."
            value={watch('question4.solution')}
          />
          <TextArea
            {...register('question4.originStory')}
            id="originStory"
            label="The Defining Moment"
            placeholder="Describe the moment or event that shaped your approach..."
            value={watch('question4.originStory')}
          />
        </>
      );
    }

    case 5:
      return (
        <div className="space-y-4">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <TextArea
                {...register(`question5.resources.${index}`)}
                id={`resource-${index}`}
                label={`Resource #${index + 1}`}
                placeholder={
                  index === 0
                    ? 'e.g., A retirement readiness checklist'
                    : index === 1
                    ? 'e.g., A Social Security timing calculator'
                    : 'e.g., A specific chapter from a recommended book'
                }
                value={watch(`question5.resources.${index}`)}
                className="min-h-[100px]"
              />
            </motion.div>
          ))}
        </div>
      );

    case 6:
      return (
        <>
          <TextArea
            {...register('question6.scarcity')}
            id="scarcity"
            label="I can only take on so many clients because..."
            placeholder="I build every plan by hand — I don't hand clients off to junior advisors or automated systems"
            value={watch('question6.scarcity')}
          />
          <TextArea
            {...register('question6.missedOpportunity')}
            id="missedOpportunity"
            label="By waiting, they missed out on..."
            placeholder="Having me personally review their portfolio before the next market shift"
            value={watch('question6.missedOpportunity')}
          />
        </>
      );

    default:
      return null;
  }
}
